import type { Due } from "./api.js";

/**
 * Rendering a due date.
 *
 * The whole hazard here is that `new Date("2026-03-17")` is midnight **UTC**,
 * so formatting it in a zone west of Greenwich prints the day before. A due
 * date is a calendar fact with no zone of its own, so every conversion below
 * stays in UTC and the comparison against today is done on the strings — which
 * sort correctly because the format is fixed-width.
 */

const DAY_MS = 86_400_000;

function toUtcMs(iso: string): number | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return undefined;
  const [, year, month, day] = match;
  if (!year || !month || !day) return undefined;
  return Date.UTC(Number(year), Number(month) - 1, Number(day));
}

/** Today in the *device's* zone, as the same YYYY-MM-DD a due date carries. */
export function todayIso(now: Date = new Date()): string {
  const year = String(now.getFullYear()).padStart(4, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function isOverdue(due: Due, today: string = todayIso()): boolean {
  return due.date < today;
}

export function formatDue(due: Due, today: string = todayIso()): string {
  const label = dayLabel(due.date, today);
  return due.time ? `${label}, ${due.time}` : label;
}

function dayLabel(date: string, today: string): string {
  const at = toUtcMs(date);
  if (at === undefined) return date;

  const base = toUtcMs(today);
  if (base !== undefined) {
    const days = Math.round((at - base) / DAY_MS);
    if (days === 0) return "Today";
    if (days === 1) return "Tomorrow";
    if (days === -1) return "Yesterday";
  }

  const sameYear = base !== undefined && date.slice(0, 4) === today.slice(0, 4);
  return new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: sameYear ? undefined : "numeric",
    // Load-bearing. Without it the instant above is re-interpreted in the
    // device's zone and a due date can print as the previous day.
    timeZone: "UTC",
  }).format(new Date(at));
}

/**
 * The zone comes from the device rather than from a picker, because the
 * question a form can actually answer is "9am" — "9am where" is only ever
 * answered by where the person is standing. The server refuses a time without a
 * zone, so the two are built together or not at all.
 */
export function dueFromInputs(date: string, time: string): Due | null {
  if (!date) return null;
  if (!time) return { date, time: null, tz: null };
  return { date, time, tz: Intl.DateTimeFormat().resolvedOptions().timeZone };
}

/**
 * A bare zero, however it was typed: `0`, `00`, `0.0`, `0,0`, `-0`, `.0`.
 * Nothing after it, so `0g` and `0.5` are not zeros but things to buy.
 */
const BARE_ZERO = /^[+-]?(?:0+(?:[.,]0*)?|[.,]0+)$/;

/**
 * A quantity as a Shopping row shows it, or `undefined` for none (g58).
 *
 * The field is free text, not an amount: `½ loaf`, `a dozen` and `1/2` are
 * all quantities, so nothing here parses a number. `parseFloat` would read
 * `0g` and `0.5` as nothing to buy, and `parseInt` would read `½` as nothing
 * at all. Only a blank or a bare zero is hidden, and only here: the stored
 * value is never touched. A number is accepted defensively, though the API
 * only ever sends a string.
 */
export function displayQuantity(value: string | number | null | undefined): string | undefined {
  if (typeof value === "number") return value === 0 || Number.isNaN(value) ? undefined : String(value);
  const shown = value?.trim();
  if (!shown || BARE_ZERO.test(shown)) return undefined;
  return shown;
}
